/**
 * PublicShell — the universal chrome for unauthenticated, no-app-shell
 * pages (sign-in, get-started, public menu). A light-grey background with
 * a top bar carrying the Ordo wordmark on the left and, on the right, an
 * optional page action (e.g. "Create an account") + a "Get Help" button.
 *
 * Scalable: every public surface wraps its content in this so the chrome
 * stays consistent in one place. Admin-authored pre-login announcement bars
 * are opt-in via `showAnnouncements` (sign-in only today).
 */

import { Button } from '@ordo/ui/button'
import { OrdoLogo } from '@ordo/ui/logos'
import { Toaster } from '@ordo/ui/sonner'
import type { ReactNode } from 'react'

import { PreLoginBanners } from '@/components/banners'
import { supportMailto } from '@/lib/support'

export interface PublicShellProps {
  children: ReactNode
  /** Vertically center the content (sign-in / get-started cards). Top-aligned otherwise. */
  center?: boolean
  /** Optional action rendered in the top bar, left of "Get Help" (e.g. a "Create an account" button). */
  headerAction?: ReactNode
  /**
   * Render admin-authored pre-login announcement bars above the chrome.
   * Opt-in per page, not shell-wide: a "trouble signing in?" notice belongs on
   * the sign-in page, not on the public menu. Sign-in is the only caller today.
   */
  showAnnouncements?: boolean
}

export function PublicShell({ children, center = false, headerAction, showAnnouncements = false }: PublicShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      {/* Above the chrome, spanning the full width — mirrors where the
          authenticated app shell stacks its announcement bars. */}
      {showAnnouncements ? <PreLoginBanners /> : null}
      <header className="flex items-center justify-between border-b border-border bg-background px-6 py-3">
        <OrdoLogo className="h-8 w-auto" />
        <div className="flex items-center gap-2">
          {headerAction}
          <Button variant="ghost" size="sm" asChild>
            <a href={supportMailto()}>Get Help</a>
          </Button>
        </div>
      </header>
      <main className={center ? 'flex flex-1 items-center justify-center p-4' : 'flex-1'}>{children}</main>
      <Toaster />
    </div>
  )
}
